import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Container from '@/components/base/Container';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Seo from '@/components/feature/Seo';
import TopicCard from '@/components/base/TopicCard';
import { categories, topics } from '@/mocks/topics';

const statusOptions = ['全部状态', '跟进中', '有新进展', '已归档'];

export default function Tracking() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || 'all';
  const query = searchParams.get('q') || '';
  const activeStatus = searchParams.get('status') || '全部状态';
  const [keyword, setKeyword] = useState(query);

  const buildCatLink = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') {
      next.delete('cat');
    } else {
      next.set('cat', id);
    }
    const queryString = next.toString();
    return queryString ? `/tracking?${queryString}` : '/tracking';
  };

  const handleStatus = (status: string) => {
    const next = new URLSearchParams(searchParams);
    if (status === '全部状态') {
      next.delete('status');
    } else {
      next.set('status', status);
    }
    setSearchParams(next);
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    const trimmed = keyword.trim();
    if (trimmed) {
      next.set('q', trimmed);
    } else {
      next.delete('q');
    }
    setSearchParams(next);
  };

  const filtered = topics.filter((topic) => {
    const catMatch = activeCat === 'all' || topic.category === activeCat;
    const statusMatch = activeStatus === '全部状态' || topic.status === activeStatus;
    const text = `${topic.title}${topic.summary}${topic.tags.join('')}${topic.categoryName}`;
    const queryMatch = !query || text.includes(query);
    return catMatch && statusMatch && queryMatch;
  });

  const activeCategoryName =
    categories.find((category) => category.id === activeCat)?.name || '全部';

  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '全部追踪 - 看料社',
    description: '看料社正在追踪的全部热点线索，可按分类、状态与关键词筛选。',
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filtered.map((topic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: topic.title,
        url: `${typeof window !== 'undefined' ? window.location.origin : ''}/topic/${topic.slug}`,
      })),
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '' },
      {
        '@type': 'ListItem',
        position: 2,
        name: '全部追踪',
        item: `${typeof window !== 'undefined' ? window.location.origin : ''}/tracking`,
      },
    ],
  };

  return (
    <>
      <Seo
        title={`${activeCategoryName}追踪列表 - 看料社热点跟进`}
        description="看料社正在追踪的全部热点线索汇总，支持按主题分类、跟进状态和关键词筛选，每条线索都保留完整时间线，方便你持续跟进最新动态。"
        keywords="追踪列表,热点线索,事件跟进,最新动态,看料社"
        jsonLd={[collectionJsonLd, breadcrumbJsonLd]}
      />

      <section className="border-b border-background-200 bg-background-100 py-8 md:py-12">
        <Container>
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '全部追踪' }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">全部追踪</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里汇集了所有正在跟进的线索。你可以按主题分类浏览，也可以筛选跟进状态，或者直接搜索关键词，快速定位你想持续关注的那一条。
          </p>
        </Container>
      </section>

      <section className="bg-background-50 py-10 md:py-14">
        <Container>
          <div className="flex flex-col gap-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <form onSubmit={handleSearch} className="flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索标题、标签或分类"
                  aria-label="搜索追踪线索"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <button
                type="submit"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-filter-3-line"></i>
                筛选
              </button>
            </form>

            <div className="flex flex-wrap items-center gap-2">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={buildCatLink(category.id)}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-1.5 text-sm transition-colors ${
                    activeCat === category.id
                      ? 'border-primary-500 bg-primary-600 text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
                  }`}
                >
                  {category.name}
                </Link>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 border-t border-background-200 pt-4">
              <span className="text-xs text-foreground-500">跟进状态</span>
              <div className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
                {statusOptions.map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => handleStatus(status)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1 text-xs font-medium transition-colors ${
                      activeStatus === status
                        ? 'bg-foreground-950 text-background-50'
                        : 'text-foreground-700 hover:text-foreground-950'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
              <span className="ml-auto text-xs text-foreground-500">
                共 {filtered.length} 条线索
              </span>
            </div>
          </div>

          {query && (
            <p className="mt-6 text-sm text-foreground-600">
              正在搜索：<span className="font-medium text-primary-700">{query}</span>
            </p>
          )}

          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((topic) => (
                <TopicCard key={topic.slug} topic={topic} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <i className="ri-search-eye-line text-4xl text-foreground-400"></i>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-900">没有匹配的追踪线索</p>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                换一个关键词，或者先清空筛选条件看看全部正在跟进的内容。
              </p>
              <Link
                to="/tracking"
                className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-refresh-line"></i>
                清空筛选
              </Link>
            </div>
          )}
        </Container>
      </section>
    </>
  );
}